/**
 * Share a binder as a link. The owner gets a short token from the server the
 * first time (the same one after that), and the link opens the binder in the
 * app when it is installed or a teaser page on the web when it is not.
 */

import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { supabase } from '../lib/supabase';

export const SHARE_ORIGIN = 'https://app.cardchase.org';

/**
 * The origin a printed QR carries, without the scheme and in capitals.
 *
 * Both are about modules. A QR's readability is the physical size of one
 * module, a phone camera wants 0.4mm of it, and a 12mm label has room for about
 * 25 modules across - so every character the code has to carry is real estate.
 * Dropping "https://" saves eight of them, and capitals put the whole string in
 * QR's alphanumeric mode, which is nearly twice as dense as mixed case. Domains
 * are case insensitive and the route accepts either, so the capitals are free.
 *
 * The bare domain, not app.cardchase.org, and no path segment before the code.
 * Those four characters and two slashes are the whole difference between a 25
 * module code and a 21 module one - 0.40mm per module against 0.46mm on a 12mm
 * label, where the floor for a phone camera is 0.40mm. cardchase.org redirects
 * /<code> to the app, and claims the same path for universal links, so a phone
 * with the app installed never sees the web at all.
 */
export const QR_ORIGIN = 'CARDCHASE.ORG';

/**
 * What a label's QR points at: the card, by its short code.
 *
 * Not the binder - a card has exactly one `binder_id`, so the code names the
 * card and the binder follows it. The ?b= parameter the long link needed is
 * gone, along with both UUIDs.
 *
 * Null when the card has no code yet, which is a card written before the column
 * existed. Printing no QR beats printing one that leads nowhere.
 */
export function labelQrUrl(card) {
  const code = card?.short_code;
  return code ? `${QR_ORIGIN}/${String(code).toUpperCase()}` : null;
}

export function shareUrlFor(token) {
  return `${SHARE_ORIGIN}/b/${token}`;
}

/** Mint or reuse the binder's share token. Throws if the caller is not the owner. */
export async function ensureShareToken(binder) {
  if (binder?.share_token) return binder.share_token;
  const { data, error } = await supabase.rpc('ensure_binder_share_token', { p_binder_id: binder.id });
  if (error) throw error;
  return data;
}

/**
 * Open the share sheet for a binder. Returns { url, method } where method is
 * 'share' (native or web share sheet) or 'clipboard'.
 */
export async function shareBinder(binder) {
  const token = await ensureShareToken(binder);
  const url = shareUrlFor(token);
  const title = binder.name ? `${binder.name} on CardChase` : 'My binder on CardChase';
  // "Green Binder binder" read badly. The word only goes on when the name lacks it.
  const text = binder.name
    ? (/\bbinder\b/i.test(binder.name)
      ? `Check out my ${binder.name} on CardChase`
      : `Check out my ${binder.name} binder on CardChase`)
    : 'Check out my binder on CardChase';

  // Closing the share sheet without picking anything is not a failure.
  const cancelled = (e) => /cancel|abort/i.test(String(e?.message || e?.errorMessage || e?.name || ''));

  if (Capacitor.isNativePlatform()) {
    try { await Share.share({ title, text, url, dialogTitle: 'Share binder' }); }
    catch (e) { if (!cancelled(e)) throw e; return { url, token, method: 'cancelled' }; }
    return { url, token, method: 'share' };
  }
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try { await navigator.share({ title, text, url }); }
    catch (e) { if (!cancelled(e)) throw e; return { url, token, method: 'cancelled' }; }
    return { url, token, method: 'share' };
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
  }
  return { url, token, method: 'clipboard' };
}

/** Resolve a token to its binder and owner (used inside the app to jump to the real binder). */
export async function resolveSharedBinder(token) {
  const { data, error } = await supabase.rpc('get_shared_binder', { p_token: token });
  if (error) throw error;
  return data || null;
}
